import { useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Chip, Icon, Stack, Text, tokens } from '@/design';
import { layerMatchesQuery } from '@/components/layers/layerModel';

interface MapSearchResultsProps {
  catalog: Layer[];
  onSelect(layer: Layer): void;
}

export function MapSearchResults({ catalog, onSelect }: MapSearchResultsProps) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => {
    if (!query.trim()) return [];
    return catalog.filter((layer) => layerMatchesQuery(layer, query)).slice(0, 8);
  }, [catalog, query]);

  return (
    <div
      style={{
        position: 'absolute',
        top: tokens.space[7],
        left: tokens.space[2],
        width: tokens.width.popoverLg,
        maxWidth: '100%',
        zIndex: tokens.zIndex.panel,
      }}
    >
      <div
        style={{
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[1],
          padding: tokens.space[2],
        }}
      >
        <label style={{ display: 'grid', gap: tokens.space[1] }}>
          <Text variant="caption" as="span">
            Search and geocode
          </Text>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search labels, ids, and kinds"
            style={{
              minHeight: tokens.density.touchMin,
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
              background: tokens.color.surface[2],
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[2],
            }}
          />
        </label>

        {results.length > 0 ? (
          <div role="listbox" aria-label="Geocode results" style={{ marginTop: tokens.space[2] }}>
            <Stack direction="column" gap={1}>
              {results.map((layer) => (
                <button
                  key={layer.id}
                  type="button"
                  role="option"
                  aria-label={`Fly to ${layer.label ?? layer.id}`}
                  onClick={() => onSelect(layer)}
                  style={{
                    appearance: 'none',
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    background: tokens.color.surface[2],
                    color: tokens.color.ink.primary,
                    textAlign: 'left',
                    minHeight: tokens.density.touchMin,
                    paddingInline: tokens.space[2],
                    paddingBlock: tokens.space[1],
                    cursor: 'pointer',
                  }}
                >
                  <Stack direction="row" align="center" gap={1}>
                    <Icon name="pin_drop" size={20} decorative />
                    <Stack direction="column" gap={1} style={{ minWidth: 0 }}>
                      <Text variant="body" as="span">
                        {layer.label ?? layer.id}
                      </Text>
                      <Chip icon="layers" label={layer.kind} variant="neutral" />
                    </Stack>
                  </Stack>
                </button>
              ))}
            </Stack>
          </div>
        ) : null}
      </div>
    </div>
  );
}
